import _ from 'lodash';
import {
  buildHiddenElement,
  buildInteractionElement,
} from './buildHiddenElement';

import type { CompRef } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { HiddenElementsScope } from '../hiddenElementsScope';

export async function refreshHiddenElements(scope: HiddenElementsScope) {
  const { editorAPI, store } = scope;

  // sometimes dm transformations are not applied
  // immediately, so calling this without waiting
  // may fetch outdated list
  await editorAPI.waitForChangesAppliedAsync();

  const focusedPageId = editorAPI.pages.getFocusedPageId();
  const sortByAttributes = ['desktopLayout.y', 'desktopLayout.x', 'compId'];

  store.setMasterPageHiddenElements(
    _.sortBy(getHiddenCompsForPage(scope, 'masterPage'), sortByAttributes),
  );
  store.setInteractionsHiddenElements(
    _.sortBy(getInteractionsHiddenComps(scope), sortByAttributes),
  );
  store.setFocusedPageHiddenElements(
    _.sortBy(getHiddenCompsForPage(scope, focusedPageId), sortByAttributes),
  );
}

export const getHiddenCompsForPage = (
  scope: HiddenElementsScope,
  pageId: string,
) => {
  const { editorAPI } = scope;
  return editorAPI.mobile.hiddenComponents.get(pageId).map((compId) => {
    return buildHiddenElement(scope, compId, pageId);
  });
};

import type { HiddenElement } from '../hiddenElementsTypes';

export const getInteractionsHiddenComps = (
  scope: HiddenElementsScope,
): HiddenElement[] => {
  const { editorAPI } = scope;
  const { variants } = editorAPI;
  if (!variants) {
    return [];
  }

  const pageRef = editorAPI.pages.getFocusedPage();

  return (
    editorAPI.components.transformations
      // @ts-expect-error
      .getAllHiddenComponentsByDefault(pageRef)
      .filter((compRef: CompRef) => !isExplicitlyVisible(editorAPI, compRef))
      .map(
        (compRef: CompRef): HiddenElement =>
          buildInteractionElement(scope, compRef, pageRef),
      )
  );
};

/**
 * Components that appear on hover interactions are hidden on mobile by default,
 * but user can explicitly show them. This function checks if a component is
 * explicitly set to be visible.
 */
const isExplicitlyVisible = (editorAPI: EditorAPI, compRef: CompRef) => {
  const siteMobileVariant = editorAPI.variants.mobile.get();
  const compVariantPointer = editorAPI.components.variants.getPointer(
    compRef,
    siteMobileVariant,
  );
  const compMobileTransformations =
    editorAPI.components.transformations.get(compVariantPointer);

  return compMobileTransformations?.hidden === false;
};
